// src/components/TreeEditorSegment.jsx
import {
  Card,
  Typography,
  Slider,
  Space,
  Button,
  Divider,
  Tooltip,
} from "antd";
import { DeleteOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState, useCallback } from "react";
import { TREES } from "../ThreeJs/Global/variables";

const { Text } = Typography;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const TreeEditorSegment = ({ selectedTree }) => {
  // ✅ No conditional hooks
  const trunk = selectedTree?.userData?.trunk || null;
  const foliage = selectedTree?.userData?.foliage || null;
  const arrow = selectedTree?.userData?.arrow || null;

  const trunkBase = selectedTree?.userData?.__trunkBaseHeight ?? 1.2;
  const arrowOffset = selectedTree?.userData?.__arrowOffset ?? 2.1;

  const foliageType = useMemo(() => {
    const t = foliage?.geometry?.type || "";
    if (t.includes("Sphere")) return "sphere";
    if (t.includes("Cone")) return "cone";
    return "unknown";
  }, [foliage]);

  const foliageBase = useMemo(() => {
    const g = foliage?.geometry;
    const p = g?.parameters || {};
    const baseSphereRadius = typeof p.radius === "number" ? p.radius : 0.9;
    const baseConeHeight = typeof p.height === "number" ? p.height : 1.8;
    return {
      sphereRadius: baseSphereRadius,
      coneHeight: baseConeHeight,
      coneHalfHeight: baseConeHeight / 2,
    };
  }, [foliage]);

  // ✅ lock state (mirrors selectedTree.userData.locked)
  const [locked, setLocked] = useState(false);

  // ✅ Controlled UI state (smooth realtime sliders)
  const [trunkGirth, setTrunkGirth] = useState(1); // drives x & z
  const [trunkH, setTrunkH] = useState(1);

  // Top controls:
  // - sphere: radius (xz), height (y)
  // - cone: base thickness (xz), height (y)
  const [topXZ, setTopXZ] = useState(1);
  const [topY, setTopY] = useState(1);

  // Sync slider values when selection changes
  useEffect(() => {
    if (!selectedTree || !trunk) {
      setTrunkGirth(1);
      setTrunkH(1);
      setTopXZ(1);
      setTopY(1);
      setLocked(false);
      return;
    }

    setLocked(!!selectedTree.userData?.locked);

    // girth: use avg of x and z (in case user had them different before)
    const x = Number(trunk.scale.x || 1);
    const z = Number(trunk.scale.z || 1);
    setTrunkGirth((x + z) / 2);

    setTrunkH(Number(trunk.scale.y || 1));

    if (foliage) {
      setTopXZ(Number(foliage.scale.x || 1));
      setTopY(Number(foliage.scale.y || 1));
    } else {
      setTopXZ(1);
      setTopY(1);
    }
  }, [selectedTree, trunk, foliage]);

  // Keep trunk on ground + foliage bottom on trunk top + arrow above trunk top
  const alignTree = useCallback(
    (nextTrunkH, nextTopY) => {
      if (!trunk) return;

      const h = clamp(nextTrunkH, 0.2, 6);

      // trunk is centered; keep its base on ground
      trunk.position.y = (trunkBase * h) / 2;
      const trunkTopY = trunkBase * h;

      if (foliage) {
        let halfHeightScaled = 0;

        if (foliageType === "sphere") {
          halfHeightScaled =
            foliageBase.sphereRadius * clamp(nextTopY, 0.2, 8);
        } else if (foliageType === "cone") {
          halfHeightScaled =
            foliageBase.coneHalfHeight * clamp(nextTopY, 0.2, 8);
        } else {
          halfHeightScaled =
            foliageBase.coneHalfHeight * clamp(nextTopY, 0.2, 8);
        }

        // bottom sits on trunkTopY
        foliage.position.y = trunkTopY + halfHeightScaled;
      }

      if (arrow) {
        arrow.position.y = trunkTopY + arrowOffset;
      }
    },
    [trunk, foliage, arrow, trunkBase, arrowOffset, foliageType, foliageBase]
  );

  const toggleLock = () => {
    if (!selectedTree) return;

    const next = !locked;
    selectedTree.userData.locked = next;
    setLocked(next);

    // Hide/show arrow based on lock
    if (selectedTree.userData?.arrow) {
      selectedTree.userData.arrow.visible = !next;
    }

    // If locking: detach any transform controls attached to this tree
    try {
      if (next && window.transformControls?.object === selectedTree) {
        window.transformControls.detach();
      }
    } catch (e) {}
  };

  // --- Handlers ---
  const setTrunkGirthBoth = (val) => {
    if (!trunk || locked) return;
    const g = clamp(val, 0.2, 5);
    trunk.scale.x = g;
    trunk.scale.z = g;
    setTrunkGirth(g);
  };

  const setTrunkHeight = (val) => {
    if (!trunk || locked) return;
    const h = clamp(val, 0.2, 6);
    trunk.scale.y = h;
    setTrunkH(h);
    alignTree(h, topY);
  };

  const setTopRadiusOrBase = (val) => {
    if (!foliage || locked) return;
    const xz = clamp(val, 0.2, 8);
    foliage.scale.x = xz;
    foliage.scale.z = xz;
    setTopXZ(xz);
  };

  const setTopHeight = (val) => {
    if (!foliage || locked) return;
    const y = clamp(val, 0.2, 8);
    foliage.scale.y = y;
    setTopY(y);
    alignTree(trunkH, y);
  };

  const deleteTree = () => {
    if (!selectedTree) return;
    const tree = selectedTree;

    try {
      if (window.transformControls?.object === tree) {
        window.transformControls.detach();
      }
    } catch (e) {}

    tree.parent?.remove(tree);

    tree.traverse((o) => {
      if (o.geometry) o.geometry.dispose?.();
      if (o.material) {
        if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
        else o.material.dispose?.();
      }
    });

    const idx = TREES?.trees?.indexOf(tree);
    if (idx !== -1) TREES.trees.splice(idx, 1);

    window.dispatchEvent(
      new CustomEvent("TREE_SELECTED", { detail: { tree: null } })
    );
  };

  // Safe render return after hooks
  if (!selectedTree || !trunk) return null;

  return (
    <Card size="small" style={{ width: 375 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text strong>Selected Tree</Text>

        <Space>
          <Tooltip title={locked ? "Unlock position" : "Lock position"}>
            <Button
              type="text"
              icon={locked ? <UnlockOutlined /> : <LockOutlined />}
              onClick={toggleLock}
            />
          </Tooltip>

          <Tooltip title="Delete Tree">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={deleteTree}
            />
          </Tooltip>
        </Space>
      </Space>

      <Divider style={{ margin: "10px 0" }} />

      {/* TRUNK */}
      <div style={{ marginBottom: 10 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary">Trunk Girth</Text>
          <Text type="secondary">{trunkGirth.toFixed(2)}</Text>
        </Space>
        <Slider
          min={0.2}
          max={5}
          step={0.05}
          value={trunkGirth}
          onChange={setTrunkGirthBoth}
          disabled={locked}
        />
      </div>

      <div style={{ marginBottom: 0 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text type="secondary">Trunk Height</Text>
          <Text type="secondary">{trunkH.toFixed(2)}</Text>
        </Space>
        <Slider
          min={0.2}
          max={6}
          step={0.05}
          value={trunkH}
          onChange={setTrunkHeight}
          disabled={locked}
        />
      </div>

      {/* TOP */}
      {foliage && (
        <>
          <Divider style={{ margin: "10px 0" }} />

          {foliageType === "sphere" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text type="secondary">Vegetation Spread</Text>
                  <Text type="secondary">{topXZ.toFixed(2)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topXZ}
                  onChange={setTopRadiusOrBase}
                  disabled={locked}
                />
              </div>

              <div style={{ marginBottom: 0 }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text type="secondary">Vegetation Height</Text>
                  <Text type="secondary">{topY.toFixed(2)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topY}
                  onChange={setTopHeight}
                  disabled={locked}
                />
              </div>
            </>
          )}

          {foliageType === "cone" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text type="secondary">Vegetation Spread</Text>
                  <Text type="secondary">{topXZ.toFixed(2)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topXZ}
                  onChange={setTopRadiusOrBase}
                  disabled={locked}
                />
              </div>

              <div style={{ marginBottom: 0 }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text type="secondary">Vegetation Height</Text>
                  <Text type="secondary">{topY.toFixed(2)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topY}
                  onChange={setTopHeight}
                  disabled={locked}
                />
              </div>
            </>
          )}

          {foliageType === "unknown" && (
            <Text type="secondary">
              Top controls unavailable (unknown foliage type).
            </Text>
          )}
        </>
      )}
    </Card>
  );
};

export default TreeEditorSegment;
